import React, { useState, useEffect } from "react";
import {
  Card,
  CardActions,
  CardContent,
  Grid,
  Box,
  Typography,
} from "@mui/material";
import FolderIcon from "@mui/icons-material/Folder";
import PlayCircleOutlineIcon from "@mui/icons-material/PlayCircleOutline";
import TextButtonComponet from "../../atom/Buttons/TextButton";
import { IMAGES } from "../../../assets/Images";
import TimerOutlinedIcon from "@mui/icons-material/TimerOutlined";
import defaultQuizThumbnail from "../../../assets/Images/quiz_thumbnail.png";
import defaultFolderThumbnail from "../../../assets/Images/folder_thumb.png";
import { renderTruncatedText } from "../../../core/Constant";

const QuizMarkCard = ({
  isFolder = false,
  title,
  onViewFolder = () => {},
  status,
  date,
  duration,
  attempts,
  onViewQuiz = () => { },
  onViewAttempsts = () => { },
  isResume = false,
  thumbnail,
  isSelectionMode = false,
  selected = false,
  onSelect = () => {},
}) => {

  // Resolve default fallback images based on whether it's a folder or a quiz
  const defaultImage = isFolder
    ? defaultFolderThumbnail
    : defaultQuizThumbnail || IMAGES?.quizIcon;

  const [imgSrc, setImgSrc] = useState(thumbnail || defaultImage);

  useEffect(() => {
    setImgSrc(thumbnail || defaultImage);
  }, [thumbnail, defaultImage]);

  return (
    <Card
      sx={{
        width: 270,
        minWidth: 270,
        maxWidth: 270,
        height: 410,
        backgroundColor: "#FFFFFF",
        borderRadius: "20px",
        border: "1px solid #EAEAEA",
        boxShadow: "0px 4px 16px rgba(0,0,0,0.06)",
        transition: "all 0.3s ease",
        display: "flex",
        flexDirection: "column",
        justifyContent: "space-between",
        position: "relative",
        p: 1.5,
        boxSizing: "border-box",
        "&:hover": {
          transform: "translateY(-4px)",
          boxShadow: "0px 8px 24px rgba(0,0,0,0.12)",
        },
      }}
    >
      {/* Top Image / Folder Frame */}
      <Box
        sx={{
          height: 160,
          width: "100%",
          borderRadius: "14px",
          backgroundColor: "#ffffff",
          border: "2.5px solid #rgba(0,0,0,0.18)",
          display: "flex",
          alignItems: "center",
          justifyContent: "center",
          overflow: "hidden",
          p: 0.5,
          boxShadow: "inset 0px 0px 8px rgba(108, 43, 217, 0.15)",
          boxSizing: "border-box",
        }}
      >
        {/*Render dynamic thumbnail or default thumbnail asset */}
        {imgSrc ? (
          <Box
            component="img"
            src={imgSrc}
            alt={title || (isFolder ? "Folder Thumbnail" : "Quiz Thumbnail")}
            onError={() => {
              setImgSrc(defaultImage);
            }}
            sx={{
              width: "100%",
              height: "100%",
              objectFit: "contain",
              borderRadius: "13px",
            }}
          />
        ) : isFolder ? (
          /* Revert to FolderIcon if image source is entirely null */
          <FolderIcon sx={{ fontSize: 90, color: "#6C2BD9" }} />
        ) : (
          /* Fallback Quiz Thumbnail */
          <Box
            component="img"
            src={defaultQuizThumbnail || IMAGES?.quizIcon}
            alt="Quiz Default Thumbnail"
            sx={{
              width: "100%",
              height: "100%",
              objectFit: "cover",
              borderRadius: "13px",
            }}
          />
        )}
      </Box>

      {/* Optional Header Date */}
      {date && (
        <Box sx={{ position: "absolute", top: 12, right: 16 }}>
          <Typography sx={{ fontSize: 10, color: "#8C8C8C" }}>
            {date}
          </Typography>
        </Box>
      )}

      {/* Main Content Area */}
      <CardContent
        sx={{
          p: 0,
          py: 1,
          flexGrow: 1,
          display: "flex",
          flexDirection: "column",
          alignItems: "center",
          justifyContent: "center",
          textAlign: "center",
          "&:last-child": { pb: 0 },
        }}
      >
        {/* Title */}
        <Typography
          title={title}
          sx={{
            color: "#4A1B8C",
            fontWeight: 800,
            fontSize: "0.95rem",
            textTransform: "uppercase",
            mb: 0.8,
            px: 0.5,
            display: "-webkit-box",
            WebkitLineClamp: 2,
            WebkitBoxOrient: "vertical",
            overflow: "hidden",
            textOverflow: "ellipsis",
            lineHeight: 1.25,
            minHeight: "2.5em",
          }}
        >
              {renderTruncatedText(title, { limit: 40 })}
        </Typography>

        {/* Quiz Information Section */}
        {!isFolder && (
          <Grid
            container
            direction="column"
            alignItems="center"
            justifyContent="center"
            spacing={0.2}
          >
            {/* Timer Icon */}
            <Grid item>
            <TimerOutlinedIcon sx={{ fontSize: 32, color: "#9834F0" }} />
          </Grid>
          {/* Duration */}
            <Grid item>
            <Typography
               sx={{
                 fontWeight: 600,
                  fontSize: "0.85rem",
                  color: "#1D2939",
                }}
              >
                {duration ? `${duration} minutes` : ""}
              </Typography>
              </Grid>

            {/* Attempts Left */}
          <Grid item>
              <Typography
                sx={{
                  fontWeight: 500,
                  fontSize: "0.8rem",
                  color: "#475467",
                }}
              >
                {attempts !== undefined && !isSelectionMode ? `${attempts} Attempts Left` : ""}
              </Typography>
          </Grid>
          </Grid>
          )}
      </CardContent>

      {/* Action Buttons Section */}
      <CardActions sx={{ p: 0, pt: 0.5, width: "100%" }}>
        <Grid container flexDirection={"column"} gap={1} width="100%">
          {isFolder ? (
            <Grid item sx={{ width: "100%" }}>
              <TextButtonComponet
                onButtonClick={onViewFolder}
                text={"View Folder"}
              />
            </Grid>
          ) : isSelectionMode ? (
            <Grid item sx={{ width: "100%" }}>
              <TextButtonComponet
                onButtonClick={onSelect}
                text={selected ? "Selected" : "Select"}
                classStyle={selected ? "btn btn-secondary" : "btn btn-primary"}
                isDisabled={selected}
              />
            </Grid>
          ) : !status ? (
            <>
              <Grid item sx={{ width: "100%" }}>
                <TextButtonComponet
                  onButtonClick={onViewAttempsts}
                  text={"View Marks"}
                />
              </Grid>

              <Grid item sx={{ width: "100%" }}>
                {attempts > -1 ? (
                  isResume ? (
                    <button
                      type="button"
                      className="btn btn-resume-quiz"
                      onClick={onViewQuiz}
                      style={{
                        width: "100%",
                        display: "flex",
                        alignItems: "center",
                        justifyContent: "center",
                        gap: "6px",
                        fontSize: "0.85rem",
                        padding: "8px 12px",
                      }}
                    >
                      <PlayCircleOutlineIcon className="btn-resume-quiz__icon" />
                      Resume Quiz
                    </button>
                  ) : (
                  <TextButtonComponet
                    onButtonClick={onViewQuiz}
                    classStyle="btn btn-secondary"
                    text={"Re Attempt"}
                  />
                  )
                ) : null}
              </Grid>
            </>
          ) : (
            <Grid item sx={{ width: "100%" }}>
              <TextButtonComponet
                onButtonClick={onViewQuiz}
                text={"Take a Test"}
              />
            </Grid>
          )}
        </Grid>
      </CardActions>
    </Card>
  );
};

export default QuizMarkCard;